import React, { useEffect, useState } from "react";
import { LuPencil, LuPlus, LuImage } from "react-icons/lu";
import { db, storage } from "../utils/firebase";
import { collection, getDocs, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { ref, listAll, deleteObject } from "firebase/storage";
import EditProductModal from "./EditProductModel";
import AddProductModal from "./AddProductModal";

const PAGE_SIZE = 20;
const inputCls = "border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:border-ring transition-colors bg-card";

function ProductsTable({ onAdd, onDelete }) {
  const [products,          setProducts]          = useState([]);
  const [editingProduct,    setEditingProduct]    = useState(null);
  const [showAddModal,      setShowAddModal]      = useState(false);
  const [showBulkModal,     setShowBulkModal]     = useState(false);
  const [selectedProducts,  setSelectedProducts]  = useState([]);
  const [filterCategory,    setFilterCategory]    = useState("");
  const [sortPrice,         setSortPrice]         = useState("");
  const [filterStock,       setFilterStock]       = useState("");
  const [filterVisibility,  setFilterVisibility]  = useState("");
  const [page,              setPage]              = useState(1);

  const fetchProducts = async () => {
    const snap = await getDocs(collection(db, "products"));
    setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => { fetchProducts(); }, []);

  const handleToggleHidden = async (productId, currentHidden) => {
    try {
      await updateDoc(doc(db, "products", productId), { hidden: !currentHidden });
      sessionStorage.removeItem("allProducts"); // bust customer-facing cache immediately
      setProducts((prev) =>
        prev.map((p) => p.id === productId ? { ...p, hidden: !currentHidden } : p)
      );
    } catch (err) {
      console.error(err);
      alert("Failed to update product visibility.");
    }
  };

  const deleteStorageFolder = async (path) => {
    try {
      const { items } = await listAll(ref(storage, path));
      await Promise.all(items.map((item) => deleteObject(item)));
    } catch { /* folder may not exist — ignore */ }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Delete this product? This cannot be undone.")) return;
    try {
      await Promise.all([
        deleteStorageFolder(`productImages/${productId}`),
        deleteStorageFolder(`heroBanners/${productId}`),
      ]);
      await deleteDoc(doc(db, "products", productId));
      setSelectedProducts((prev) => prev.filter((id) => id !== productId));
      onDelete?.();
      await fetchProducts();
    } catch (err) {
      console.error(err);
      alert("Failed to delete product. Please try again.");
    }
  };

  const toggleSelect = (id) =>
    setSelectedProducts((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const filteredProducts = products
    .filter((p) => filterCategory ? p.category === filterCategory : true)
    .filter((p) => filterStock === "in" ? p.quantity > 0 : filterStock === "out" ? p.quantity <= 0 : true)
    .filter((p) => filterVisibility === "visible" ? !p.hidden : filterVisibility === "hidden" ? p.hidden : true)
    .sort((a, b) => sortPrice === "high" ? b.price - a.price : sortPrice === "low" ? a.price - b.price : 0);

  const categoryOptions = [...new Set(products.map((p) => p.category).filter(Boolean))].sort();

  const totalPages      = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
  const paginatedProducts = filteredProducts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset to page 1 and clear selection when filters or sort change
  const resetPage = () => { setPage(1); setSelectedProducts([]); };

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3">
        <div className="flex flex-wrap gap-3">
          <select
            value={filterCategory}
            onChange={(e) => { setFilterCategory(e.target.value); resetPage(); }}
            className={`${inputCls} w-full sm:w-auto`}
          >
            <option value="">All Categories</option>
            {categoryOptions.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <select value={sortPrice} onChange={(e) => { setSortPrice(e.target.value); resetPage(); }} className={`${inputCls} w-full sm:w-auto`}>
            <option value="">Sort by Price</option>
            <option value="low">Price: Low → High</option>
            <option value="high">Price: High → Low</option>
          </select>
          <select value={filterStock} onChange={(e) => { setFilterStock(e.target.value); resetPage(); }} className={`${inputCls} w-full sm:w-auto`}>
            <option value="">All Stock</option>
            <option value="in">In Stock</option>
            <option value="out">Out of Stock</option>
          </select>
          <select value={filterVisibility} onChange={(e) => { setFilterVisibility(e.target.value); resetPage(); }} className={`${inputCls} w-full sm:w-auto`}>
            <option value="">All Visibility</option>
            <option value="visible">Visible</option>
            <option value="hidden">Hidden</option>
          </select>
        </div>
        <div className="flex gap-3">
          {selectedProducts.length > 0 && (
            <>
              <button
                onClick={() => setSelectedProducts([])}
                className="text-xs text-slate-400 hover:text-red-500 font-medium transition-colors"
              >
                Clear ({selectedProducts.length})
              </button>
              <button
                onClick={() => setShowBulkModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-accent/10 border border-accent/20 text-accent font-bold text-sm rounded-xl hover:bg-brand-navy hover:text-white transition-colors"
              >
                <LuPencil size={16} />
                Bulk Edit ({selectedProducts.length})
              </button>
            </>
          )}
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand-navy hover:bg-brand-amber transition-colors text-white font-bold text-sm rounded-xl"
          >
            <LuPlus size={16} />
            Add Product
          </button>
        </div>
      </div>

      {/* ── Mobile card list (< md) ───────────────────────────── */}
      {paginatedProducts.length === 0 ? (
        <div className="md:hidden bg-card rounded-2xl border border-border p-12 text-center text-[var(--color-muted-foreground)] text-sm">
          No products found.
        </div>
      ) : (
        <div className="md:hidden space-y-3">
          {/* Select-all bar */}
          <div className="flex items-center gap-3 px-1">
            <input
              type="checkbox"
              className="accent-brand-amber"
              checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
              onChange={(e) =>
                e.target.checked
                  ? setSelectedProducts(filteredProducts.map((p) => p.id))
                  : setSelectedProducts([])
              }
            />
            <span className="text-xs text-[var(--color-muted-foreground)] font-medium">Select all</span>
          </div>

          {paginatedProducts.map((prod) => (
            <div
              key={prod.id}
              className={`bg-card border rounded-2xl p-4 transition-colors ${selectedProducts.includes(prod.id) ? "border-brand-amber/40 bg-accent/5" : "border-border"} ${prod.hidden ? "opacity-60" : ""}`}
            >
              <div className="flex gap-3">
                {/* Checkbox */}
                <div className="flex flex-col items-center gap-2.5 flex-shrink-0 pt-0.5">
                  <input
                    type="checkbox"
                    className="accent-brand-navy"
                    checked={selectedProducts.includes(prod.id)}
                    onChange={() => toggleSelect(prod.id)}
                  />
                  {prod.imageUrls?.[0] ? (
                    <img src={prod.imageUrls[0]} alt={prod.name} className="w-14 h-14 object-cover rounded-xl" />
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-muted flex items-center justify-center">
                      <LuImage size={20} className="text-[var(--color-muted-foreground)]" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-bold text-sm text-foreground leading-snug">{prod.name}</p>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        <span className="text-xs bg-[#eef4ff] dark:bg-accent/10 text-brand-navy dark:text-accent font-semibold px-2 py-0.5 rounded-full">{prod.category}</span>
                        {prod.hidden && (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-100 dark:bg-white/10 px-1.5 py-0.5 rounded">Hidden</span>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="font-black text-sm text-foreground">${Number(prod.price).toFixed(2)}</p>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full mt-1 inline-block ${prod.quantity > 0 ? "bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-400" : "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400"}`}>
                        {prod.quantity > 0 ? `Qty: ${prod.quantity}` : "Out of Stock"}
                      </span>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => setEditingProduct(prod)}
                      className="flex-1 py-2 bg-brand-navy dark:bg-accent dark:text-on-accent text-white text-xs font-bold rounded-lg hover:bg-brand-amber dark:hover:opacity-90 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleHidden(prod.id, !!prod.hidden)}
                      className={`flex-1 py-2 text-xs font-bold rounded-lg transition-colors ${prod.hidden ? "bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-950/40 dark:text-green-400 dark:hover:bg-green-950/60" : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-300 dark:hover:bg-white/20"}`}
                    >
                      {prod.hidden ? "Show" : "Hide"}
                    </button>
                    <button
                      onClick={() => handleDelete(prod.id)}
                      className="flex-1 py-2 bg-red-50 text-red-600 text-xs font-bold rounded-lg hover:bg-red-100 dark:bg-red-950/40 dark:text-red-400 dark:hover:bg-red-950/60 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Desktop table (≥ md) ──────────────────────────────── */}
      <div className="hidden md:block bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
        {paginatedProducts.length === 0 ? (
          <div className="p-12 text-center text-[var(--color-muted-foreground)] text-sm">No products found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-brand-navy text-white">
                  <th className="px-4 py-3.5 text-left">
                    <input
                      type="checkbox"
                      className="accent-brand-amber"
                      checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                      onChange={(e) =>
                        e.target.checked
                          ? setSelectedProducts(filteredProducts.map((p) => p.id))
                          : setSelectedProducts([])
                      }
                    />
                  </th>
                  <th className="px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wider">Image</th>
                  <th className="px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wider">Price</th>
                  <th className="px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wider">Qty</th>
                  <th className="px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wider">Category</th>
                  <th className="px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {paginatedProducts.map((prod) => (
                  <tr key={prod.id} className={`hover:bg-muted transition-colors ${selectedProducts.includes(prod.id) ? "bg-accent/5" : ""} ${prod.hidden ? "opacity-50" : ""}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        className="accent-brand-navy"
                        checked={selectedProducts.includes(prod.id)}
                        onChange={() => toggleSelect(prod.id)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      {prod.imageUrls?.[0] ? (
                        <img src={prod.imageUrls[0]} alt={prod.name} className="h-11 w-11 object-cover rounded-lg" />
                      ) : (
                        <div className="h-11 w-11 rounded-lg bg-muted flex items-center justify-center">
                          <LuImage size={20} className="text-[var(--color-muted-foreground)]" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-[200px]">
                      <span className="font-semibold text-foreground truncate block">{prod.name}</span>
                      {prod.hidden && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-100 dark:bg-white/10 dark:text-slate-400 px-1.5 py-0.5 rounded mt-0.5 inline-block">Hidden</span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-bold text-foreground">${Number(prod.price).toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${prod.quantity > 0 ? "bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-400" : "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400"}`}>
                        {prod.quantity > 0 ? prod.quantity : "Out"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs bg-[#eef4ff] dark:bg-accent/10 text-brand-navy dark:text-accent font-semibold px-2.5 py-1 rounded-full">{prod.category}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingProduct(prod)}
                          className="px-3 py-1.5 bg-brand-navy dark:bg-accent dark:text-on-accent text-white text-xs font-bold rounded-lg hover:bg-brand-amber dark:hover:opacity-90 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleToggleHidden(prod.id, !!prod.hidden)}
                          className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${prod.hidden ? "bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-950/40 dark:text-green-400 dark:hover:bg-green-950/60" : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-300 dark:hover:bg-white/20"}`}
                        >
                          {prod.hidden ? "Show" : "Hide"}
                        </button>
                        <button
                          onClick={() => handleDelete(prod.id)}
                          className="px-3 py-1.5 bg-red-50 text-red-600 text-xs font-bold rounded-lg hover:bg-red-100 dark:bg-red-950/40 dark:text-red-400 dark:hover:bg-red-950/60 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-400">
            Page {page} of {totalPages} · {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredProducts.length)} of {filteredProducts.length} products
            {selectedProducts.length > 0 && ` · ${selectedProducts.length} selected across all pages`}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 text-xs font-bold rounded-xl border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              ← Prev
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 text-xs font-bold rounded-xl border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {editingProduct && (
        <EditProductModal product={editingProduct} onClose={() => setEditingProduct(null)} onUpdate={fetchProducts} />
      )}
      {showAddModal && (
        <AddProductModal onClose={() => setShowAddModal(false)} onAdd={() => { onAdd?.(); fetchProducts(); }} />
      )}
      {showBulkModal && (
        <EditProductModal
          product={products.find((p) => p.id === selectedProducts[0]) || {}}
          selectedIds={selectedProducts}
          onClose={() => setShowBulkModal(false)}
          onUpdate={fetchProducts}
        />
      )}
    </div>
  );
}

export default ProductsTable;
